import type { RadioSnapshot } from "@agency/shared";
import { audioPrefs } from "../audio/audioPrefs.js";
import type { RadioPlayerState } from "../radio/RadioPlayer.js";
import { setGameKeyboardEnabled } from "../runtime.js";
import { button, el } from "./dom.js";

export interface PlaybackLine {
  text: string;
  tone: "live" | "muted" | "busy" | "warn" | "error";
  /** A local recovery the listener can take. */
  action?: { label: string; aria: string };
}

/** Desired (server) plus actual (this browser) state, in words. */
export function describePlayback(desired: RadioSnapshot, local: RadioPlayerState): PlaybackLine {
  if (!desired.playing) return { text: "Radio off", tone: "muted" };
  switch (local.status) {
    case "loading":
      return { text: "Connecting…", tone: "busy" };
    case "playing":
      return local.buffering
        ? { text: "Buffering…", tone: "busy" }
        : { text: "LIVE", tone: "live" };
    case "blocked":
      return {
        text: "Your browser paused the radio.",
        tone: "warn",
        action: { label: "▶ Click to tune in", aria: `Tune in to ${desired.stationName}` },
      };
    case "error":
      return {
        text: local.error ?? "This station can't be played in your browser.",
        tone: "error",
        action: { label: "⟳ Retry", aria: `Retry ${desired.stationName}` },
      };
    default:
      return {
        text: "Not playing here.",
        tone: "warn",
        action: { label: "▶ Tune in", aria: `Tune in to ${desired.stationName}` },
      };
  }
}

/** Mute toggle plus the personal radio volume slider. */
export function volumeControl(): HTMLElement {
  const root = el("div", "radio-volume");
  const mute = button("", "radio-icon-btn");
  const slider = el("input");
  slider.type = "range";
  slider.min = "0";
  slider.max = "100";
  slider.step = "1";
  slider.setAttribute("aria-label", "Radio volume");

  const render = () => {
    const muted = audioPrefs.isMuted();
    const volume = audioPrefs.radioVolume();
    mute.textContent = muted || volume === 0 ? "🔇" : "🔊";
    mute.setAttribute("aria-label", muted ? "Unmute game audio (M)" : "Mute game audio (M)");
    mute.title = mute.getAttribute("aria-label")!;
    slider.value = String(Math.round(volume * 100));
    slider.setAttribute("aria-valuetext", `${slider.value}%`);
    slider.style.setProperty("--fill", `${slider.value}%`);
  };
  mute.addEventListener("click", () => audioPrefs.toggleMuted());
  slider.addEventListener("input", () => audioPrefs.setRadioVolume(Number(slider.value) / 100));
  audioPrefs.onChange(render);
  render();

  root.append(mute, slider);
  return root;
}

/**
 * Keep the game keyboard off while focus is inside a UI panel, and hand
 * focus back to the game after mouse clicks so WASD keeps walking.
 */
export function bindPanelFocus(root: HTMLElement, keepFocus: (target: Element) => boolean): void {
  root.dataset.uiPanel = "";
  root.addEventListener("focusin", () => setGameKeyboardEnabled(false));
  root.addEventListener("focusout", (event) => {
    const next = event.relatedTarget as Node | null;
    if (!next || !root.contains(next)) setGameKeyboardEnabled(true);
  });

  let pointer = false;
  root.addEventListener("pointerdown", () => {
    pointer = true;
  });
  const release = () => {
    if (!pointer) return;
    pointer = false;
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.contains(active) && !keepFocus(active)) active.blur();
  };
  root.addEventListener("click", release);
  root.addEventListener("change", release);

  root.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    (document.activeElement as HTMLElement | null)?.blur();
  });
}

export function stationMeta(parts: Array<string | number | null | undefined>): string {
  return parts.filter((part) => part !== null && part !== undefined && part !== "").join(" · ");
}
